import { useState, useEffect, Suspense } from 'react';
import axios from 'axios';
import dynamic from 'next/dynamic';
import { motion } from 'framer-motion';
import { useAppState } from '../../context/app';
import ObjectsCatalogSkeletonItem from '../objects/ObjectsCatalogSkeletonItem';
import Skelet from '../Skelet';
import { declOfNum, sleep } from '../../helpers';
import styles from '../../styles/modules/favorites/FavoritesList.module.scss';

const ObjectsCatalogItem = dynamic(() => import('../objects/ObjectsCatalogItem'), {
  suspense: true
});

const FavoritesList = () => {
  const appState = useAppState();
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    if (!appState.isLoaded) return;

    const fetchData = async () => {
      setIsLoading(true);
      await sleep(300);

      try {
        const response = await axios.post(process.env.NEXT_PUBLIC_API + '/favorites', {
          ads: appState.favorites,
          length: 50
        });

        if (!response.data || response.data.status !== 'ok') throw new Error(response.data.text);

        setTotal(response.data.data.total);
        setData(response.data.data.data);
        setIsLoading(false);
        setIsError(false);
      } catch (error) {
        setIsLoading(false);
        setIsError(true);
      }
    };
    fetchData();
  }, [appState.isLoaded]);

  if (isLoading || isError)
    return (
      <section className="block">
        <div className={['fixed', styles.favorites].join(' ')}>
          <div className={styles.favoritesHeader}>
            <Skelet height={33} maxWidth={200} className={styles.favoritesHeaderTitle} />
          </div>
          <div className={styles.favoritesContent}>
            {[...Array(3).keys()].map((value) => (
              <ObjectsCatalogSkeletonItem key={value} />
            ))}
          </div>
        </div>
      </section>
    );

  return (
    <section className="block">
      <div className="fixed">
        <div className={styles.favorites}>
          {!!data.length && (
            <div className={styles.favoritesHeader}>
              <div className={styles.favoritesHeaderTitle}>
                Моё избранное
                <span>
                  {total} {declOfNum(total, ['объект', 'объекта', 'объектов'])}
                </span>
              </div>
            </div>
          )}
          <div className={styles.favoritesContent}>
            {!!data.length ? (
              data.map((v, i) => (
                <Suspense fallback={<ObjectsCatalogSkeletonItem />} key={i}>
                  <ObjectsCatalogItem item={v} />
                </Suspense>
              ))
            ) : (
              <motion.div
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{
                  opacity: 1,
                  scale: 1,
                  transition: { duration: 1, type: 'spring', bounce: 0.6 }
                }}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center'
                }}
              >
                <h5 style={{ marginBottom: 8 }}>Нет избранных объектов</h5>
                <div style={{ opacity: 0.6, textAlign: 'center', margin: '0 18px' }}>
                  Жмите{' '}
                  <svg
                    style={{ position: 'relative', top: '6px', margin: '0 4px', color: '#fb5f5f' }}
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="white"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                  </svg>{' '}
                  на странице объекта и добавляйте сюда то, что нравится.
                </div>
                <img
                  src="/images/wondering-man.svg"
                  alt=""
                  style={{ width: '100%', maxWidth: 400, margin: '18px 32px' }}
                />
              </motion.div>
            )}
          </div>
          {!!data.length && (
            <div className={styles.favoritesFooter}>
              <span style={{ fontWeight: 600, marginRight: 4 }}>Внимание!</span> Избранные Вами
              объекты хранятся только на Вашем устройстве, с другого устройства они не будут
              отображаться.
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default FavoritesList;
